import type { ChatModelAdapter } from "@assistant-ui/react";
import type { Station } from "@shared/schema";

// One flattened transcript entry; what onTurnComplete reports and what the
// server stores in copy_messages.chat_history.
export type ChatTranscriptEntry = { role: "user" | "assistant"; text: string };

// Join the text parts of an assistant-ui message content array into a string.
const contentText = (content: unknown): string =>
  Array.isArray(content)
    ? content
        .filter((p: any) => p?.type === "text")
        .map((p: any) => p.text || "")
        .join("")
    : typeof content === "string"
      ? content
      : "";

// Chat transport for the Copy Engine refinement thread: POSTs the latest user
// turn + prior messages to the station-scoped chat endpoint and accumulates
// the SSE reply into one assistant message. Moved verbatim from the classic
// Copy Engine page so the workbench Copy tool can share it (formData is typed
// loosely here because each caller has its own form shape). When a turn
// completes successfully, onTurnComplete (if given) receives the full
// flattened transcript including the new reply, so callers can persist it.
export const createStationCopyEngineAdapter = (
  station: Station,
  formData?: Record<string, unknown>,
  getAuthToken?: () => Promise<string | null>,
  onTurnComplete?: (transcript: ChatTranscriptEntry[]) => void
): ChatModelAdapter => {
  return {
    async run({ messages, abortSignal }) {
      const lastMessage = messages[messages.length - 1];
      let userMessage = "";
      if (lastMessage?.content && lastMessage.content[0]) {
        const content = lastMessage.content[0];
        if (content.type === 'text') {
          userMessage = content.text || "";
        }
      }

      try {
        // Get Firebase token for authentication
        const token = getAuthToken ? await getAuthToken() : null;

        const headers: HeadersInit = {
          "Content-Type": "application/json",
        };

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`/api/stations/${station.id}/copy-engine/chat`, {
          method: "POST",
          headers,
          credentials: "include",
          signal: abortSignal,
          body: JSON.stringify({
            message: userMessage,
            previousMessages: messages.slice(0, -1),
            formData: formData, // Include form data for context
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to get response from Copy Engine");
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) throw new Error("No reader available");

        let content = "";
        let buffer = "";

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            // Buffer across reads: an SSE frame larger than one network read
            // (any reply over roughly 1.4KB) arrives split, and the old
            // per-read parsing silently dropped it - the empty-bubble bug.
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              if (!line.startsWith("data: ")) continue;
              const data = line.slice(6).trim();
              if (data === "[DONE]") continue;

              let parsed: any = null;
              try {
                parsed = JSON.parse(data);
              } catch {
                console.error("Copy Engine chat: unparseable SSE line", data.slice(0, 120));
                continue;
              }
              if (parsed?.content) {
                content += parsed.content;
              } else if (parsed?.error) {
                // Server-sent error frame. Must be thrown OUTSIDE the parse
                // try/catch - the old code caught its own throw here and the
                // error never reached the user.
                throw new Error(parsed.error);
              }
            }
          }
        } finally {
          reader.releaseLock();
        }

        // Never return an empty assistant message as success: it renders as a
        // blank bubble and poisons the thread history for every later turn.
        if (!content.trim()) {
          throw new Error("The Copy Engine returned an empty reply. Please try again.");
        }

        if (onTurnComplete) {
          try {
            const transcript: ChatTranscriptEntry[] = [
              ...messages
                .filter((m) => m.role === "user" || m.role === "assistant")
                .map((m) => ({
                  role: m.role as "user" | "assistant",
                  text: contentText(m.content),
                })),
              { role: "assistant" as const, text: content },
            ].filter((t) => t.text.trim());
            onTurnComplete(transcript);
          } catch {
            // Persistence is best-effort; the chat itself must never break.
          }
        }

        return {
          content: [{ type: "text", text: content }]
        };
      } catch (error: any) {
        if (error.name === 'AbortError') {
          console.log('Request was cancelled');
          return {
            content: [{ type: "text", text: "" }]
          };
        }

        console.error("Error in Copy Engine:", error);
        return {
          content: [{ type: "text", text: `Sorry, I encountered an error: ${error.message || 'Failed to process your request'}. Please try again.` }]
        };
      }
    }
  };
};
